import { useContext, useEffect, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { haveValue, toast } from "../../helper/common";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import CheckIcon from "../../assets/img/checkmark-circle-02.svg";
import ButtonActionTooltip from "../ErrorModal/ButtonActionTooltip";

const FlatmateOrGuarantorAddForm = ({
  isOpen,
  onClose,
  selectedUser,
  setModal,
  isGuarantor,
  handleSendEmailToGuarantor,
}) => {
  const { _t } = useTranslator();
  const { user, loadUser, getUserByEmail } = useContext(AuthContext);
  const {
    deleteSelectedOnboardingUser,
    addSharer,
    createBasicGuarantorAccount,
  } = useContext(TenantContext);
  const [addFlatmateLoading, setAddFlatmateLoading] = useState(false);
  const [guarantorAccountPresent, setGuarantorAccountPresent] = useState(false);

  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    relation: "",
  });

  useEffect(() => {
    if (haveValue(selectedUser?.name)) {
      const nameParts = selectedUser.name.trim().split(/\s+/);

      let fname = "";
      let lname = "";

      if (nameParts.length === 2) {
        fname = nameParts[0];
        lname = nameParts[1];
      } else if (nameParts.length > 2) {
        const mid = Math.floor(nameParts.length / 2);
        fname = nameParts.slice(0, mid).join(" ");
        lname = nameParts.slice(mid).join(" ");
      } else {
        fname = nameParts[0];
        lname = "";
      }

      setFormData({
        fname,
        lname,
      });
    } else {
      setFormData({
        fname: "",
        lname: "",
        email: "",
        relation: "",
      });
    }
  }, [selectedUser]);

  const handleInputChange = (e) => {
    if (isGuarantor) {
      setGuarantorAccountPresent(false);
    }
    let { name, value } = e.target;
    if (name === "email") {
      value = value?.toLowerCase();
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitSharer = async () => {
    try {
      setAddFlatmateLoading(true);
      const allFilled =
        haveValue(formData.fname) &&
        haveValue(formData.lname) &&
        haveValue(formData.email) &&
        haveValue(formData.relation);

      if (!allFilled) return;
      if (isGuarantor) {
        const payload = {
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          relation: formData.relation,
          step: 1,
        };
        const response = await createBasicGuarantorAccount(payload);
        if (response?.status === "success") {
          handleSendEmailToGuarantor(formData.email);
        } else {
          toast.error(_t("failedToAddGuarantor"));
        }
      } else {
        const payload = {
          name: formData.fname + " " + formData.lname,
          email: formData.email,
          relation: formData.relation,
        };

        const res = await addSharer(payload);
        if (haveValue(selectedUser)) {
          const payload = {
            tenant_id: user?._id,
            ...selectedUser,
          };
          await deleteSelectedOnboardingUser(payload);
        }
        if (!res?.success) {
          let errorMessage = res?.response?.data?.message;
          if (errorMessage && isGuarantor) {
            errorMessage = _t("failedToAddGuarantor");
          } else if (errorMessage) {
            // keep the same error message
            errorMessage = _t(errorMessage);
          } else {
            errorMessage = _t("failedToAddSharer");
          }
          toast.error(errorMessage);
          return;
        }
        loadUser("tenant");
        closeModel();
      }
    } catch (err) {
      if (
        isGuarantor &&
        err?.response?.data?.errorKey === "account-already-present"
      ) {
        handleSendEmailToGuarantor(formData.email);
        loadUser("tenant");
        return;
      }
      toast.error(
        (err?.response?.data?.message ?? isGuarantor)
          ? _t("failedToAddGuarantor")
          : _t("failedToAddSharer")
      );
    } finally {
      setAddFlatmateLoading(false);
    }
  };

  const closeModel = () => {
    setFormData({
      fname: "",
      lname: "",
      email: "",
      relation: "",
    });
    onClose();
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkEmailPresent = async () => {
    const response = await getUserByEmail({
      email: formData.email,
      type: "tenant",
    });

    if (response) {
      const user = response.user;
      if (user) {
        setGuarantorAccountPresent(true);
      }
    }
  };

  const disableButton =
    !haveValue(formData.fname) ||
    !haveValue(formData.lname) ||
    !haveValue(formData.email) ||
    !haveValue(formData.relation) ||
    !isValidEmail(formData.email);

  if (!isOpen) return null;

  return (
    <div
      className={`modal wrapper-modal show rentSharerDetailsModal fade ${
        isGuarantor ? "modal-fixed-screen" : ""
      }`}
      style={{ display: "block" }}
      onClick={() => setModal(false)}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header justify-content-between">
            <h5 className="modal-title">
              {isGuarantor ? _t("guarantorDetails") : _t("rentSharerDetails")}
            </h5>
            <div className="d-flex align-items-center custom-gap-2">
              {isGuarantor && guarantorAccountPresent && (
                <button className="bankStatementButton bankAddedButton guarantorAlreadyPresent">
                  <img src={CheckIcon} />
                  {_t("guarantorAlreadyPresent")}
                </button>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={() => setModal(false)}
              ></button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <div className="row form-flatmate">
              {/* Form Section */}
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">{_t("firstName")}</label>
                      <input
                        type="text"
                        placeholder={_t("enterFirstName")}
                        name="fname"
                        value={formData.fname}
                        onChange={handleInputChange}
                        onBeforeInput={(e) => {
                          const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                          if (!isValidChar) e.preventDefault();
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">{_t("lastName")}</label>
                      <input
                        type="text"
                        placeholder={_t("enterLastName")}
                        name="lname"
                        value={formData.lname}
                        onChange={handleInputChange}
                        onBeforeInput={(e) => {
                          const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                          if (!isValidChar) e.preventDefault();
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">
                        {_t("relationshipWithSharer")}
                      </label>
                      <select
                        name="relation"
                        id=""
                        value={formData?.relation}
                        onChange={handleInputChange}
                        style={{
                          color:
                            (!haveValue(formData?.relation) ||
                              formData?.relation === "") &&
                            "#7f7f7f",
                        }}
                      >
                        <option value="" selected>
                          {_t("pleaseSelect")}
                        </option>
                        {(isGuarantor
                          ? [
                              { value: "parent", label: _t("parent") },
                              { value: "Family", label: _t("Family") },
                              { value: "relative", label: _t("relative") },
                              { value: "partner", label: _t("partner") },
                              { value: "company", label: _t("company") },
                              { value: "other", label: _t("other") },
                            ]
                          : [
                              { value: "friend", label: _t("friend") },
                              { value: "wife", label: _t("wife") },
                              { value: "couple", label: _t("couple") },
                              { value: "colleague", label: _t("colleague") },
                              { value: "other", label: _t("other") },
                            ]
                        ).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">
                        {isGuarantor
                          ? _t("emailOfGuarantor")
                          : _t("emailOfSharer")}
                      </label>
                      <input
                        type="email"
                        placeholder={_t("enterYourEmail")}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        onBeforeInput={(e) => {
                          if (!e.data) return; // allow delete, etc.

                          const value = e.data;

                          // Allow full pasted string
                          const isValid = /^[a-z0-9._%+-@]+$/.test(value);

                          if (!isValid) {
                            e.preventDefault();
                            return;
                          }

                          // Prevent multiple @ (handle paste too)
                          const existingValue = e.target.value;
                          const totalAtCount =
                            (existingValue + value).split("@").length - 1;

                          if (totalAtCount > 1) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={() => (isGuarantor ? checkEmailPresent() : {})}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            {isGuarantor && guarantorAccountPresent ? (
              <button
                type="button"
                className="addGuarantorButton d-flex align-items-center justify-content-center"
                onClick={() => handleSendEmailToGuarantor(formData.email)}
              >
                {_t("sendRequestToAddGuarantor")}
              </button>
            ) : (
              <div
                data-tooltip-id="button-action-tooltip"
                style={{ display: "inline-block", width: "100%" }}
              >
                <button
                  type="button"
                  className="addGuarantorButton d-flex align-items-center justify-content-center"
                  style={{
                    opacity: disableButton || addFlatmateLoading ? 0.5 : "",
                  }}
                  disabled={disableButton || addFlatmateLoading}
                  onClick={handleSubmitSharer}
                >
                  {isGuarantor ? _t("sendRequest") : _t("addFlatmate")}
                </button>
                {(disableButton || addFlatmateLoading) && (
                  <ButtonActionTooltip />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlatmateOrGuarantorAddForm;
